import React, { useEffect, useState } from "react";
import "./RecentWorksheets.css";
import {
    clearRecentWorksheets,
    forgetWorksheet,
    readRecentCollapsed,
    readRecentWorksheets,
    RECENT_WORKSHEETS_CHANGED,
    RecentWorksheet,
    writeRecentCollapsed,
} from "./RecentWorksheets.ts";
import { useTranslation } from "./i18n/useTranslation.ts";
import { format } from "./i18n/format.ts";
import { BlueprintCorners } from "./Theme/Blueprint.tsx";

/**
 * The worksheets this browser has made recently.
 *
 * Renders nothing at all until there is something to show, so a first visit is
 * unchanged and a returning one gets its sheets back. The list is read after
 * mount rather than during render: the static HTML is the same for everyone,
 * and only this browser knows what is in its own storage.
 *
 * It is read again whenever the list changes, so a sheet appears here as soon
 * as it is saved - on a first visit the panel arrives with the first sheet -
 * rather than on the next page load.
 *
 * The list collapses. It sits below the settings rather than above them, so it
 * never stands between a visitor and the generator - but at `MAX_RECENT` of 8
 * it is a few hundred pixels of a phone screen that a returning visitor has
 * already read, sitting between the settings and everything after them.
 * Measured at 375px wide with three entries: 318px open, 61px closed.
 *
 * The heading itself is the control - a separate toggle beside it would be a
 * second thing to hit on a phone, and the heading is already what a visitor
 * points at when they mean "this section".
 *
 * **It starts collapsed.** The list is a returning visitor's history rather
 * than part of making a worksheet, and the heading carries the count, so
 * nothing is hidden that a visitor cannot see is there.
 *
 * The choice is remembered, in its own storage key: opening it once and
 * finding it shut on the next visit is the bug this would otherwise be. Same
 * button-with-`aria-expanded` shape the advanced settings toggle uses
 * (`Settings/SettingsContainer.tsx`), so the two collapsibles on the page
 * behave alike.
 */
export function RecentWorksheetsPanel() {
    const [recent, setRecent] = useState<RecentWorksheet[]>([]);
    // Matches `readRecentCollapsed`'s default. The list is empty on the first
    // render either way, so nothing flashes - but a `false` here would state a
    // default the storage layer does not hold.
    const [collapsed, setCollapsed] = useState(true);
    const t = useTranslation();

    useEffect(() => {
        setRecent(readRecentWorksheets());
        setCollapsed(readRecentCollapsed());

        // A sheet saved in this tab (RECENT_WORKSHEETS_CHANGED), or in another
        // one (`storage`, which never fires in the tab that wrote).
        const reread = () => setRecent(readRecentWorksheets());
        window.addEventListener(RECENT_WORKSHEETS_CHANGED, reread);
        window.addEventListener("storage", reread);
        return () => {
            window.removeEventListener(RECENT_WORKSHEETS_CHANGED, reread);
            window.removeEventListener("storage", reread);
        };
    }, []);

    if (recent.length === 0) {
        return null;
    }

    function toggle() {
        const next = !collapsed;
        setCollapsed(next);
        writeRecentCollapsed(next);
    }

    return (
        <section className="recentWorksheets card blueprint" aria-labelledby="recentWorksheetsHeading">
            <BlueprintCorners/>
            <h2 id="recentWorksheetsHeading" className="recentHeading">
                <button
                    type="button"
                    className="recentToggle"
                    aria-expanded={!collapsed}
                    aria-controls="recentWorksheetsBody"
                    onClick={toggle}
                >
                    {t('settings.recentWorksheetsHeading')}
                    {/* The count is worth showing when the list is hidden - it is
                        the reason to open it, and the same argument the advanced
                        toggle makes for "(3 on)". */}
                    <span className="recentCount">
                        {collapsed ? ` (${recent.length})` : ""}
                    </span>
                    <span className="recentChevron" aria-hidden="true">
                        {collapsed ? "▾" : "▴"}
                    </span>
                </button>
            </h2>
            {/* Hidden rather than unmounted: `aria-controls` has to point at
                something that exists, and the count above is read from the same
                list either way. */}
            <div id="recentWorksheetsBody" hidden={collapsed}>
                <p className="recentHint">
                    {t('settings.recentWorksheetsHint')}
                </p>
                <ul className="recentList">
                    {recent.map((entry) => (
                        <li key={entry.url} className="recentItem">
                            <a className="recentLink" href={entry.url}>
                                {entry.label}
                            </a>
                            <button
                                type="button"
                                className="recentRemove"
                                aria-label={format(t('settings.removeWorksheet'), { label: entry.label })}
                                onClick={() => setRecent(forgetWorksheet(entry.url))}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
                <button
                    type="button"
                    className="recentClear"
                    onClick={() => setRecent(clearRecentWorksheets())}
                >
                    {t('settings.clearThisList')}
                </button>
            </div>
        </section>
    );
}

export default RecentWorksheetsPanel;
